import { G } from '@mobily/ts-belt';
import { DialogProps, SxProps, Theme } from '@mui/material';
import { Decimal } from 'decimal.js';
import { ReactElement } from 'react';
import { ResourceProps, SelectInputProps } from 'react-admin';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { FileUploadInput } from '@Common/types/bo/file_upload';
import { BOResourceAll } from '@Common/types/bo_external/resources';

import { TranslationKeys } from '@sh4res/bo-common/i18n/types';

export * from '@sh4res/bo-common/utils/brandStyling';
export * from '@sh4res/bo-common/utils/validators/input';

const makeFormatter =
  (keys: string[], join: string, emptyString = '-') =>
  // allow all types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (record?: { [k: string]: any }): string => {
    if (!record) {
      return 'No data provided';
    }

    const values: (string | number)[] = [];

    keys.forEach((key) => {
      const value = record[key];

      if (G.isNotNullable(value) && value !== '') {
        values.push(value);
      }
    });

    return values.join(join) || emptyString;
  };

export const formatAddress = makeFormatter(
  ['flat_building', 'street', 'city', 'postalcode', 'country_of_tax_residence'],
  ', ',
);

export const formatStreet = makeFormatter(['flat_building', 'street'], ', ');

export const formatMailingStreet = makeFormatter(['mailing_flat_building', 'mailing_street'], ', ');

export const formatFullname = makeFormatter(['firstname', 'lastname'], ' ');

export const convertCentsInUnits = (value: string | number): number => {
  if (typeof value === 'string') {
    return new Decimal(parseFloat(value)).div(100).toNumber();
  }

  return new Decimal(value).div(100).toNumber();
};

// Migrated AXA amounts are stored as micro-units (×10^6), so divide by 1,000,000 to get the base
// currency unit (e.g. euros).
export const convertMicroInUnits = (value: string | number): number => {
  if (typeof value === 'string') {
    return new Decimal(parseFloat(value)).div(1_000_000).toNumber();
  }

  return new Decimal(value).div(1_000_000).toNumber();
};

export function buildDecimalOptions(precision = 20) {
  return {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: precision,
  };
}

/**
 * Parse and convert a file to a base 64 file
 *
 * @param file A file imported via a FileInput
 *
 * @returns A promise containing the base 64 file
 */
export const convertFileToBase64 = async (file: {
  rawFile?: File;
  title: string;
}): Promise<{ name: string; file: string | null }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve({ name: file.title, file: reader.result as string });

    reader.onerror = () => reject('Error importing file');

    if (!file.rawFile) {
      return;
    }

    reader.readAsDataURL(file.rawFile);
  });

export async function createFileUploadInput({
  rawFile,
  title,
}: {
  rawFile: File;
  title: string;
}): Promise<FileUploadInput> {
  const { file: base64Content } = await convertFileToBase64({
    rawFile,
    title,
  });

  if (base64Content === null) {
    throw new Error('Empty file');
  }

  return {
    file_type: rawFile.type,
    file_extension: rawFile.name.split('.').pop() ?? '',
    file_content: base64Content,
  };
}

export type Resources = Exclude<BOResourceAll, '*'>;

export type ResourceDefinitionBase<TranslationKeysT extends string> = Partial<
  Pick<ResourceProps, 'list' | 'create' | 'edit' | 'show'>
> & {
  options?: {
    label?: TranslationKeysT;
    [key: string]: unknown;
  };
};

export type ResourceDefinitionCommon = ResourceDefinitionBase<TranslationKeys>;

export type ColumnList<T extends object> = Record<keyof T, ReactElement>;

export type RaSort<T extends BoListQueryBase> = {
  field: Exclude<T['sort'], undefined>[0];
  order: Exclude<T['sort'], undefined>[1];
};

export function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Unknown error';
}

/**
 * Wraps a dialog close handler so that clicking the backdrop no longer closes
 * the dialog.
 */
export const disableBackdropClose =
  (onClose: () => void): NonNullable<DialogProps['onClose']> =>
  (_event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

export const getSpaceAboveFooter = () => {
  const footer = document.getElementById('footer');

  if (!footer) {
    return '40px';
  }

  const footerRect = footer.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  if (footerRect.top < viewportHeight) {
    const spaceAboveFooter = viewportHeight - footerRect.top;
    return `${spaceAboveFooter + 20}px`;
  } else {
    return '40px';
  }
};

/**
 * Returns symmetric difference of two arrays
 * Having two arrays [1,2,3] and [2,3,4] their symmetric difference would be [1,4].
 */
export function arraySymmetricDifference<T>(array: T[], otherArray: T[]): T[] {
  return array
    .filter((x) => !otherArray.includes(x))
    .concat(otherArray.filter((x) => !array.includes(x)));
}

export function isEmpty(value: unknown, checkString = false): boolean {
  const isNullishValue = typeof value === 'undefined' || value === null || value === '';
  const isNullishText = value === 'undefined' || value === 'null';
  return checkString ? isNullishValue || isNullishText : isNullishValue;
}

export type FileInputValue = {
  // check for actual type, inlined for now
  rawFile: File;
  src: string;
  title: string;
};

export type TCurrencies = 'GBP' | 'USD' | 'EUR';

export const buildCurrencyOptions = (
  currency: TCurrencies,
  precision?: number,
  currencyDisplay: 'code' | 'symbol' = 'code',
): Intl.NumberFormatOptions => ({
  style: 'currency',
  currency,
  compactDisplay: 'long',
  currencyDisplay,
  maximumFractionDigits: precision,
});

export const formatCentsNumber = (
  value: number,
  locale = 'fr-FR',
  currency: TCurrencies = 'EUR',
  minimumFractionDigits = 0,
) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    compactDisplay: 'long',
    currencyDisplay: 'symbol',
    minimumFractionDigits,
    maximumFractionDigits: 2,
  }).format(new Decimal(value).div(100).toNumber());

// oxlint-disable-next-line typescript/ban-types -- allow {} here
export type LooseAutocomplete<T extends string> = T | (string & {});

export function makeChoicesCommon<T extends string>() {
  return (
    choicesMap: Record<T, TranslationKeys>,
  ): Array<{
    id: T;
    name: (typeof choicesMap)[T];
  }> =>
    Object.entries(choicesMap).map(([key, value]) => ({
      id: key as T,
      name: value as (typeof choicesMap)[keyof typeof choicesMap],
    }));
}

export const isValueSet = (value: string | null | undefined) =>
  value !== undefined && value !== '' && value !== null;

/** `filterToQuery` for autocompletes searching by numeric id only */
export const idOnlyFilterToQuery = (value: string) =>
  /^\d+$/.test(value) ? { id: parseInt(value) } : {};

// Use this props instead of disabled
// When using disabled, react-hook-form un-registers the input and the value is not
// included when submitting but the form throws validation errors from react-admin
export const inputDisabledProps = (
  readonly: boolean,
  sx?: SxProps<Theme>,
): Partial<Pick<SelectInputProps, 'helperText' | 'inputProps' | 'sx' | 'readOnly'>> =>
  readonly
    ? {
        helperText: false,
        inputProps: {
          readOnly: true,
          tabIndex: -1, // prevent keyboard focus
        },
        sx: {
          pointerEvents: 'none',
          userSelect: 'none',
          tabIndex: -1,
          color: (theme) => theme.palette.text.disabled,
          '.MuiInputBase-input': {
            color: (theme) => theme.palette.text.disabled,
          },
          '.MuiSvgIcon-root': {
            color: (theme) => theme.palette.text.disabled,
          },
          opacity: 0.8,
          cursor: 'not-allowed',
          '& .MuiCheckbox-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiChip-label': {
            color: (theme) => theme.palette.text.disabled,
          },
          '& .MuiFilledInput-underline:before': {
            borderBottomStyle: 'dotted',
          },
          '& .MuiFilledInput-underline:after': {
            borderBottomStyle: 'dotted',
          },
          '& .MuiFormHelperText-root': {
            display: 'none',
          },
          // Scoped to the actual input box rather than the whole root (which also spans the
          // label row for 'standard'/'outlined' variants) so the grey fill doesn't bleed above
          // the field. Covers 'filled' too — MuiFilledInput-root always carries this class.
          '& .MuiInputBase-root': {
            pointerEvents: 'none',
            backgroundColor: (theme) => theme.palette.action.disabledBackground,
          },
          '& .MuiInputBase-input': {
            userSelect: 'none',
          },
          ...sx,
        },
      }
    : {
        sx,
      };
